import { inject, Inject, Injectable } from '@angular/core';
import { CookieService } from "ngx-cookie-service";
import { Router } from "@angular/router";
import { JwtHelperService } from '@auth0/angular-jwt';
import { TokensService } from '../http-services/tokens.service';
import { LoginRequestModel, TokensResponseModel, UserInfoResponseModel } from '@itb-monorepo/models';
import { DOCUMENT } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { UsersService } from '../http-services/users.service';
import { TradersService } from '../http-services/traders.service';
import { AUTH_SCOPE_TOKEN } from '@itb-monorepo/enums';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public tokensService = inject(TokensService);
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private jwtService = inject(JwtHelperService);
  private usersService = inject(UsersService);
  private tradersService = inject(TradersService);
  private doc = inject(DOCUMENT);
  private scope = inject(AUTH_SCOPE_TOKEN);
  tokenPrefix = '';
  private loggingOut = false;
  get isLoggingOut(): boolean {
    return this.loggingOut;
  }
  constructor() {
    if (this.scope == 'admins') {
      this.tokenPrefix = 'admin-';
    }
  }

  async signIn(
    formGroup: LoginRequestModel,
    returnUrl?: string
  ): Promise<void> {
    this.loggingOut = false;

    this.usersService.clearCurrentUser();
    this.tradersService.clearCurrentTrader();

    const res = await firstValueFrom(this.tokensService.loginUser(formGroup));

    this.setTokenInCookie(res);

    sessionStorage.removeItem('auth_return_url');

    await this.redirectAfterAuthentication(returnUrl);
  }
  async redirectAfterAuthentication(returnUrl?: string): Promise<void> {
    const targetUrl = returnUrl ?? '/dashboard';
    const safeUrl = this.getSafeReturnUrl(targetUrl);

    if (!safeUrl) {
      await this.router.navigateByUrl('/dashboard');
      return;
    }

    const currentOrigin = this.doc.location.origin;

    if (safeUrl.origin === currentOrigin) {
      const internalUrl = safeUrl.pathname + safeUrl.search + safeUrl.hash;

      await this.router.navigateByUrl(internalUrl);
      return;
    }

    this.doc.location.assign(safeUrl.href);
  }
  private getSafeReturnUrl(targetUrl: string): URL | null {
    try {
      const currentOrigin = this.doc.location.origin;
      const parsedUrl = new URL(targetUrl, currentOrigin);

      const isSameOrigin = parsedUrl.origin === currentOrigin;
      if (!isSameOrigin && parsedUrl.protocol !== 'https:') {
        return null;
      }

      const allowedDomains = new Set<string>([
        'itbfx.net',
        'itbfx.com',
        'itbfx.co',
        'itbfx.pro',
        'itbfx.info',
        'itbfx.vip',
      ]);

      const hostname = parsedUrl.hostname.toLowerCase();

      const normalizedHostname = hostname.startsWith('www.')
        ? hostname.slice(4)
        : hostname;

      const isMainWebsite = allowedDomains.has(normalizedHostname);

      const isCrmWebsite =
        normalizedHostname.startsWith('crm.') &&
        allowedDomains.has(normalizedHostname.slice(4));

      if (!isMainWebsite && !isCrmWebsite) {
        return null;
      }

      return parsedUrl;
    } catch {
      return null;
    }
  }
  get isAuthenticated(): boolean {
    let result = false;
    const token = this.cookieService.get(this.tokenPrefix + 'access-token');
    if (token) result = !this.jwtService.isTokenExpired(token);
    return result;
  }

  get isTokenExpired(): boolean {
    let result = false;
    const token = this.cookieService.get(this.tokenPrefix + 'access-token');
    if (token) result = this.jwtService.isTokenExpired(token);
    return result;
  }
  get hasRefreshToken(): boolean {
    let result = false;
    const refreshToken = this.cookieService.get('refresh-token');
    if (refreshToken) result = true;
    return result;
  }
  async logout(): Promise<void> {
    this.loggingOut = true;

    await firstValueFrom(this.tokensService.logoutUser());

    this.usersService.clearCurrentUser();
    this.tradersService.clearCurrentTrader();

    this.clearCookies();

    await this.router.navigate(['auth', 'sign-in']);
  }
  getUserInfo(): UserInfoResponseModel {
    return <UserInfoResponseModel>(
      this.jwtService.decodeToken<UserInfoResponseModel>()
    );
  }

  refreshToken() {
    this.tokensService.refreshAccessToken().subscribe(
      (res: TokensResponseModel) => {
        this.setTokenInCookie(res);
      },
      (error) => {
        this.usersService.clearCurrentUser();
        this.tradersService.clearCurrentTrader();
        this.clearCookies();
      }
    );
  }

  hasAccess(required: string[] | string): boolean {
    const scopes = this.getUserInfo().scopes.map((scope) =>
      scope.toLowerCase()
    );
    const requiredPermissions = Array.isArray(required) ? required : [required];
    const normalizedRequired = requiredPermissions.map((r) => r.toLowerCase());

    return normalizedRequired.some((req) => {
      const [reqRes, reqAct] = req.split(':');
      return scopes.some((scope) => {
        const [res, acts] = scope.split(':');
        return (
          (res === reqRes || res === '*') &&
          acts?.toUpperCase().includes(reqAct.toUpperCase())
        );
      });
    });
  }

  clearCookies(): void {
    let domain = this.doc.location.hostname;
    const domainSplit = domain.split('.');
    if (domainSplit.length > 2) {
      domain = domainSplit.slice(-2).join('.');
    }
    if (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.clear instanceof Function
    ) {
      sessionStorage.clear();
      this.cookieService.delete(this.tokenPrefix + 'access-token', '/', domain);
      this.cookieService.delete('refresh-token', '/', domain);
    }
  }

  setTokenInCookie(tokens: TokensResponseModel) {
    let domain = this.doc.location.hostname;
    if (domain.includes('itbfx')) {
      const domainSplit = domain.split('.');
      if (domainSplit.length > 2) {
        domain = domainSplit.slice(-2).join('.');
      }
    }

    this.cookieService.delete(this.tokenPrefix + 'access-token', '/', domain);
    this.cookieService.delete('refresh-token', '/', domain);
    const accessTokenExpiresAt = new Date(
      tokens.access_token_expires_at * 1000
    );
    this.cookieService.set(
      this.tokenPrefix + 'access-token',
      tokens.access_token,
      accessTokenExpiresAt,
      '/',
      `.${domain}`
    );
    if (tokens.refresh_token) {
      const refreshTokenExpiresAt = new Date(
        tokens.refresh_token_expires_at * 1000
      );
      this.cookieService.set(
        'refresh-token',
        tokens.refresh_token,
        refreshTokenExpiresAt,
        '/',
        `.${domain}`
      );
    }
  }
}
